// Reading a dashboard row (.claude/features/12900-rms-dashboards.md). Pure: nothing here touches the
// network or the page, so the list's model and its unit tests can use it without the hooks.

import type { RmsView } from "../Data/views";
import type {
  DashboardWidget,
  DashboardConfig,
  DashboardTab,
  DashboardView,
} from "./data";

/**
 * A tab from the views API as a `DashboardTab`, or null when it is not one. The API always
 * answers `widgets`, a stored row's `cards` included (#12954); `cards` is read here only for
 * deploy skew, a page talking to a backend not yet on this change. Either way the tab is handed
 * on as `widgets`, so a save writes the new key. A widget saved with a data scope (before #12938)
 * reads as one without: the key is dropped, so nothing renders under it and a save never
 * writes it back.
 */
const parseTab = (value: unknown): DashboardTab | null => {
  if (typeof value !== "object" || value === null) return null;
  const { cards, ...tab } = value as {
    id?: unknown;
    widgets?: unknown;
    cards?: unknown;
  };
  if (typeof tab.id !== "string") return null;
  const widgets = tab.widgets ?? cards;
  if (!Array.isArray(widgets)) return null;
  return {
    ...tab,
    widgets: widgets.map((widget: DashboardWidget): DashboardWidget => {
      const read: DashboardWidget & { scope?: unknown } = { ...widget };
      delete read.scope;
      return read;
    }),
  } as DashboardTab;
};

/** A stored block's tabs, or null when it is not a dashboard's. */
const parseTabs = (value: unknown): DashboardTab[] | null => {
  if (typeof value !== "object" || value === null) return null;
  const tabs = (value as { tabs?: unknown }).tabs;
  if (!Array.isArray(tabs)) return null;
  const parsed = tabs.map(parseTab);
  if (parsed.some((tab) => tab === null)) return null;
  return parsed as DashboardTab[];
};

/**
 * A views-API row as a dashboard, or null when it is not one: the one place a row becomes a
 * `DashboardView`, so nothing downstream reads a dashboard block that is not there.
 */
export const parseDashboard = (row: RmsView): DashboardView | null => {
  const config = row.config;
  if (row.view_type !== "dashboard") return null;
  const tabs = parseTabs(config.dashboard);
  if (tabs === null) return null;
  // No kind is a mixed dashboard ("Mixed records"), read as null whether stored null or absent.
  // The block is its tabs alone: a classification label an older row still stores is dropped
  // here, so it is never written back (a dashboard's category is its view group, #12953).
  const dashboard: DashboardConfig = { tabs };
  return {
    ...row,
    config: { ...config, entity_kind: config.entity_kind ?? null, dashboard },
  } as DashboardView;
};

/** The number of cards a dashboard holds, over all its tabs. */
export const dashboardWidgetCount = (view: DashboardView): number =>
  view.config.dashboard.tabs.reduce((sum, tab) => sum + tab.widgets.length, 0);

/**
 * The number of library components a dashboard's widgets use, over all its tabs: a component
 * placed as two widgets counts once. Sharing the dashboard shares these (#12952).
 */
export const dashboardComponentCount = (dashboard: DashboardConfig): number =>
  new Set(
    dashboard.tabs.flatMap((tab) =>
      tab.widgets.map((widget) => widget.component_id)
    )
  ).size;
